import { RefObject, useEffect, useRef } from 'react';
import Hls, { HlsConfig } from 'hls.js';

import { Video } from '../types';

const CLOUDFRONT_URL = process.env.REACT_APP_AWS_CLOUDFRONT_URL;
const HLS_TYPE = 'application/vnd.apple.mpegurl';
// Without a start time, iOS loads nothing before play(): the video stays black.
const FIRST_FRAME = '#t=0.001';

// Same source as the feed player: native HLS (Safari), hls.js elsewhere, the mp4 for old videos
// or a broken stream. Everything is released when the video changes or the element unmounts.
const useHlsSource = (
  videoRef: RefObject<HTMLVideoElement>,
  video: Pick<Video, 'path' | 'stream'>,
  config: Partial<HlsConfig>,
) => {
  const videoUrl = `${CLOUDFRONT_URL}${video.path}`;
  // TEMPORARY: ?exp=mp4 tests the iOS swipe freeze without HLS.
  const noHls = window.location.search.includes('exp=mp4');
  const streamUrl = video.stream && !noHls ? `${CLOUDFRONT_URL}${video.stream}` : '';
  const hasVideo = Boolean(video.path || video.stream);
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    // An empty player of the mobile feed: no video yet.
    if (!el || !hasVideo) {
      return undefined;
    }
    let hls: Hls | null = null;

    const fallBack = () => {
      el.removeEventListener('error', fallBack);
      if (hls) {
        hls.destroy();
        hls = null;
        hlsRef.current = null;
      }
      el.src = videoUrl + FIRST_FRAME;
    };

    if (!streamUrl) {
      el.src = videoUrl + FIRST_FRAME;
    } else if (el.canPlayType(HLS_TYPE)) {
      el.addEventListener('error', fallBack);
      el.src = streamUrl + FIRST_FRAME;
    } else if (Hls.isSupported()) {
      hls = new Hls(config);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          fallBack();
        }
      });
      hls.loadSource(streamUrl);
      hls.attachMedia(el);
      hlsRef.current = hls;
    } else {
      el.src = videoUrl + FIRST_FRAME;
    }

    return () => {
      el.removeEventListener('error', fallBack);
      if (hls) {
        hls.destroy();
        hlsRef.current = null;
        return;
      }
      el.removeAttribute('src');
      el.load();
    };
  }, [videoRef, hasVideo, streamUrl, videoUrl, config]);

  return hlsRef;
};

export default useHlsSource;
